import { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { Form, Input, InputNumber, Select, Switch, DatePicker, Button, message, Modal, Row, Col } from 'antd';
import { FormField } from '../../admin/components/FormBuilder';
import { supabase, functions } from '@/shared/lib/supabase';

const { TextArea } = Input;
const { Option } = Select;

interface FormRendererProps {
  formId: string;
  formName: string;
  fields: FormField[];
  settings: {
    submitButtonText?: string;
    successMessage?: string;
  };
  clientProductId: string;
  initialValues?: Record<string, any>;
  readOnly?: boolean;
  onSuccess?: () => void;
  submissionId?: string;
  onDraftCreated?: (id: string) => void;
  isPreviouslyApproved?: boolean;
}

// CPF validation
const validateCPF = (cpf: string): boolean => {
  cpf = cpf.replace(/[^\d]/g, '');
  
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  
  let sum = 0;
  let remainder;
  
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
  }
  
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(9, 10))) return false;
  
  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
  }
  
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(10, 11))) return false;
  
  return true;
};

// CNPJ validation
const validateCNPJ = (cnpj: string): boolean => {
  cnpj = cnpj.replace(/[^\d]/g, '');
  
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(cnpj)) return false;
  
  let length = cnpj.length - 2;
  let numbers = cnpj.substring(0, length);
  const digits = cnpj.substring(length);
  let sum = 0;
  let pos = length - 7;
  
  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;
  
  length = length + 1;
  numbers = cnpj.substring(0, length);
  sum = 0;
  pos = length - 7;
  
  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;
  
  return true;
};

// Format masks
const formatPhone = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{2})(\d{5})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return value;
};

const formatCPF = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{3})(\d{2})$/);
  if (match) {
    return `${match[1]}.${match[2]}.${match[3]}-${match[4]}`;
  }
  return value;
};

const formatCNPJ = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/);
  if (match) {
    return `${match[1]}.${match[2]}.${match[3]}/${match[4]}-${match[5]}`;
  }
  return value;
};

export const FormRenderer = ({
  formId,
  formName,
  fields,
  settings,
  clientProductId,
  onSuccess,
  initialValues,
  readOnly,
  submissionId: submissionIdProp,
  onDraftCreated,
  isPreviouslyApproved,
}: FormRendererProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [submissionId, setSubmissionId] = useState<string | undefined>(submissionIdProp);
  const autosaveTimer = useRef<number | null>(null);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues]);

  useEffect(() => {
    // If parent passed a submissionId, keep it in local state
    setSubmissionId(submissionIdProp);
  }, [submissionIdProp]);

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);

      // Confirm final submission - warn user it cannot be edited
      Modal.confirm({
        title: isPreviouslyApproved ? 'Confirmar reenvio do formulário' : 'Confirmar envio do formulário',
        content: isPreviouslyApproved
          ? 'Ao reenviar o formulário, ele passará por nova aprovação pelos administradores. Deseja prosseguir?'
          : 'Ao enviar o formulário, ele não poderá ser editado novamente. Deseja confirmar o envio?',
        okText: isPreviouslyApproved ? 'Reenviar' : 'Confirmar envio',
        cancelText: 'Cancelar',
            onOk: async () => {
          try {
            // Ensure we have a submission id (draft). If not, create one via function
            let sid = submissionId;
              if (!sid) {
              const createResp = await functions.invoke('create-form-submission', { body: { client_product_id: clientProductId } });
              const createData = createResp?.data ?? createResp;
              sid = createData?.id;
              setSubmissionId(sid);
              if (onDraftCreated && sid) onDraftCreated(sid);
            }
            
            if (!sid) throw new Error('Não foi possível criar rascunho de formulário');

            // Try to submit via secure edge functions (get-form-session -> client-form-submit)
            try {
              const sessionResp = await functions.invoke('get-form-session', {
                body: { client_product_id: clientProductId },
              });
              const sessionData = sessionResp?.data ?? sessionResp;
              const token = sessionData?.token || sessionData?.data?.token;

              if (!token) {
                throw new Error('Falha ao obter sessão temporária para envio do formulário');
              }

              // Build payload compatible with client-form-submit which forwards to form-submission-received
              const forwardPayload: any = {
                submission_id: sid,
                form_id: formId,
                data: values,
                form_data: values, // back-compat
                n8n_execution_id: null,
                metadata: { via: 'spa' },
              };

              const submitResp = await functions.invoke('client-form-submit', {
                body: forwardPayload,
                headers: { Authorization: `Bearer ${token}` },
              });
              const submitData = submitResp?.data ?? submitResp;

              if (!submitData || submitData.error) {
                throw new Error(submitData?.error || 'Falha no envio via client-form-submit');
              }
              // success - ensure local state reflects server state
            } catch (fnErr) {
              // Fallback: attempt to directly update the form_submissions table (legacy behavior)
              console.warn('client-form-submit failed; falling back to direct DB update:', fnErr);

              // Fetch current submission to read submission_count
              const { data: current, error: fetchErr } = await supabase
                .from('form_submissions')
                .select('id, submission_count')
                .eq('id', sid)
                .maybeSingle();
              if (fetchErr) console.warn('Could not fetch current submission for final submit (fallback):', fetchErr);
              const updatedPayload: any = {
                form_data: values,
                data: values,
                form_id: formId,
                status: 'submitted',
                submitted_at: new Date().toISOString(),
                last_submitted_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                submission_count: (current?.submission_count || 0) + 1,
              };
              const { error: updateErr } = await supabase
                .from('form_submissions')
                .update(updatedPayload)
                .eq('id', sid);
              if (updateErr) throw updateErr;

              // Try to set client_products.form_approval_status to submitted to keep UI consistent
              if (clientProductId) {
                try {
                  await supabase.from('client_products').update({ form_approval_status: 'submitted', updated_at: new Date().toISOString() }).eq('id', clientProductId);
                } catch (cpErr) {
                  console.warn('Fallback: could not update client_products.form_approval_status:', cpErr);
                }
              }
            }

            message.success(settings.successMessage || (isPreviouslyApproved ? 'Formulário reenviado com sucesso!' : 'Formulário enviado com sucesso!'));
            setLastSavedAt(new Date().toISOString());
            form.resetFields();
            if (onSuccess) onSuccess();
          } catch (err) {
            console.error('Error submitting final form:', err);
            message.error('Erro ao enviar formulário. Tente novamente.');
          } finally {
            setSubmitting(false);
          }
        },
        onCancel: () => {
          setSubmitting(false);
        },
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      message.error('Erro ao enviar formulário. Tente novamente.');
    } finally {
      // setSubmitting is handled in the modal confirm closure
    }
  };

  // Autosave: debounced save on any field change
  const handleValuesChange = (_: any, values: any) => {
    if (readOnly) return;
    // Clear existing timer
    if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    // Set new timer
    autosaveTimer.current = window.setTimeout(async () => {
      try {
        setIsSaving(true);
        // Ensure we have a draft submission
        let sid = submissionId;
          if (!sid) {
          const createResp = await functions.invoke('create-form-submission', { body: { client_product_id: clientProductId } });
          const createData = createResp?.data ?? createResp;
          sid = createData?.id;
          setSubmissionId(sid);
          if (onDraftCreated && sid) onDraftCreated(sid);
        }

        if (!sid) {
          console.warn('Autosave: could not determine submission id');
          setIsSaving(false);
          return;
        }

        // Update draft fields in DB without changing status
            const { error } = await supabase.from('form_submissions').update({ form_data: values, data: values, updated_at: new Date().toISOString() }).eq('id', sid);
        if (error) {
          console.warn('Autosave error:', error);
          setIsSaving(false);
        } else {
          // Update last saved timestamp so we can show it in the UI
          setLastSavedAt(new Date().toISOString());
          // Optionally show subtle toast
          // message.success('Esboço salvo', 1);
          setIsSaving(false);
        }
      } catch (err) {
        console.warn('Autosave failed:', err);
        setIsSaving(false);
      }
    }, 800);
  };

  const renderField = (field: FormField) => {
    const commonProps = {
      placeholder: field.placeholder,
    };

    // Custom validators based on field type
    const validators: any[] = [];
    
    if (field.required) {
      validators.push({
        required: true,
        message: `${field.label} é obrigatório`,
      });
    }

    // Type-specific validation
    if (field.type === 'email') {
      validators.push({
        type: 'email',
        message: 'E-mail inválido',
      });
    }

    if (field.type === 'cpf') {
      validators.push({
        validator: (_: any, value: string) => {
          if (!value || validateCPF(value)) {
            return Promise.resolve();
          }
          return Promise.reject('CPF inválido');
        },
      });
    }

    if (field.type === 'cnpj') {
      validators.push({
        validator: (_: any, value: string) => {
          if (!value || validateCNPJ(value)) {
            return Promise.resolve();
          }
          return Promise.reject('CNPJ inválido');
        },
      });
    }

    if (field.type === 'phone') {
      validators.push({
        pattern: /^\(\d{2}\) \d{5}-\d{4}$/,
        message: 'Telefone inválido. Use o formato (00) 00000-0000',
      });
    }

    let inputComponent;

    switch (field.type) {
      case 'textarea':
        inputComponent = <TextArea {...commonProps} rows={4} />;
        break;

      case 'number':
        inputComponent = <InputNumber {...commonProps} style={{ width: '100%' }} />;
        break;

      case 'date':
        inputComponent = <DatePicker {...commonProps} style={{ width: '100%' }} format="DD/MM/YYYY" />;
        break;

      case 'select':
        inputComponent = (
          <Select {...commonProps}>
            {field.options?.map((opt) => (
              <Option key={opt} value={opt}>
                {opt}
              </Option>
            ))}
          </Select>
        );
        break;

      case 'multi-select':
        inputComponent = (
          <Select {...commonProps} mode="multiple">
            {field.options?.map((opt) => (
              <Option key={opt} value={opt}>
                {opt}
              </Option>
            ))}
          </Select>
        );
        break;

      case 'checkbox':
        inputComponent = <Switch />;
        break;

      case 'phone':
        inputComponent = (
          <Input
            {...commonProps}
            maxLength={15}
            onChange={(e) => {
              const formatted = formatPhone(e.target.value);
              form.setFieldValue(field.id, formatted);
            }}
          />
        );
        break;

      case 'cpf':
        inputComponent = (
          <Input
            {...commonProps}
            maxLength={14}
            onChange={(e) => {
              const formatted = formatCPF(e.target.value);
              form.setFieldValue(field.id, formatted);
            }}
          />
        );
        break;

      case 'cnpj':
        inputComponent = (
          <Input
            {...commonProps}
            maxLength={18}
            onChange={(e) => {
              const formatted = formatCNPJ(e.target.value);
              form.setFieldValue(field.id, formatted);
            }}
          />
        );
        break;

      case 'email':
        inputComponent = <Input {...commonProps} type="email" />;
        break;

      default:
        inputComponent = <Input {...commonProps} />;
    }

    const value = initialValues ? initialValues[field.id] : undefined;

    return (
      <Form.Item
        key={field.id}
        label={field.label}
        name={field.id}
        rules={validators}
        valuePropName={field.type === 'checkbox' ? 'checked' : 'value'}
        initialValue={field.defaultValue}
      >
        {readOnly ? (
          <div style={{ padding: '8px 0' }}>{Array.isArray(value) ? value.join(', ') : String(value ?? '')}</div>
        ) : (
          inputComponent
        )}
      </Form.Item>
    );
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px' }}>
      <h2>{formName}</h2>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        initialValues={initialValues}
      >
        <Row gutter={16}>
          {fields
            .sort((a, b) => a.order - b.order)
            .map((field) => {
              // some field types should be full width
              const fullWidthTypes = ['textarea', 'date', 'select', 'multi-select', 'radio'];
              const span = fullWidthTypes.includes(field.type as string) ? 24 : 12;
              return (
                <Col key={field.id} span={span}>
                  {renderField(field)}
                </Col>
              );
            })}
        </Row>

        {!readOnly && (
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              block
            >
              {isPreviouslyApproved ? (settings.submitButtonText || 'Reenviar') : (settings.submitButtonText || 'Enviar')}
            </Button>
          </Form.Item>
        )}
      </Form>
      {isSaving && (
        <div style={{ marginTop: 8, textAlign: 'center', color: '#888', fontSize: 12 }}>
          Salvando rascunho...
        </div>
      )}
      {!isSaving && lastSavedAt && (
        <div style={{ marginTop: 8, textAlign: 'center', color: '#888', fontSize: 12 }}>
          Último rascunho salvo: {dayjs(lastSavedAt).format('DD/MM/YYYY HH:mm:ss')}
        </div>
      )}
    </div>
  );
};
